/**
 * TA Desk access model — mirrored from backend app-configs/access-config.ts
 */

export type TaDeskCapability =
  | "ADMIN_ACCOUNT"
  | "MANAGE_CONTACTS"
  | "MANAGE_ENQUIRIES"
  | "MANAGE_QUOTES"
  | "MANAGE_QUOTE_TEMPLATES"
  | "MANAGE_BOOKINGS"
  | "MANAGE_BOOKING_FINANCE"
  | "VIEW_ALL_BOOKINGS"
  | "VIEW_TEAM_ENQUIRIES"
  | "VIEW_TEAM_QUOTES"
  | "VIEW_TEAM_BOOKINGS"
  | "VIEW_REPORTS"
  | "VIEW_ACCOUNTING"
  | "MANAGE_ACCOUNTING";

export const ALL_TA_DESK_CAPABILITIES: TaDeskCapability[] = [
  "ADMIN_ACCOUNT",
  "MANAGE_CONTACTS",
  "MANAGE_ENQUIRIES",
  "MANAGE_QUOTES",
  "MANAGE_QUOTE_TEMPLATES",
  "MANAGE_BOOKINGS",
  "MANAGE_BOOKING_FINANCE",
  "VIEW_ALL_BOOKINGS",
  "VIEW_TEAM_ENQUIRIES",
  "VIEW_TEAM_QUOTES",
  "VIEW_TEAM_BOOKINGS",
  "VIEW_REPORTS",
  "VIEW_ACCOUNTING",
  "MANAGE_ACCOUNTING",
];

export type OrganizationRoleId =
  | "travel_agent"
  | "brand_manager"
  | "bookings_team"
  | "reporter"
  | "accountant";

export const ORGANIZATION_ROLE_IDS: OrganizationRoleId[] = [
  "travel_agent",
  "brand_manager",
  "bookings_team",
  "reporter",
  "accountant",
];

export type RoleTemplateId =
  | "owner"
  | "brand_manager"
  | "travel_agent"
  | "bookings_team"
  | "reporter"
  | "accountant"
  | "custom";

export interface iBrandAccessEntry {
  brandId: string;
  roleTemplate: RoleTemplateId;
  capabilities: TaDeskCapability[];
}

export interface iUserOrgAccess {
  isOwner: boolean;
  capabilities: TaDeskCapability[];
}

export interface iUserAccess {
  org: iUserOrgAccess;
  brandAccess: iBrandAccessEntry[];
  version: 1;
}

export interface iUserOrgAccessV2 {
  isOwner: boolean;
  roles: OrganizationRoleId[];
  capabilities: TaDeskCapability[];
}

export interface iUserAccessV2 {
  org: iUserOrgAccessV2;
  brandIds: string[];
  version: 2;
}

export type StoredUserAccess = iUserAccess | iUserAccessV2;

export interface iUserTokenAccess {
  isOwner: boolean;
  capabilities?: TaDeskCapability[];
  brandIds?: string[];
  /** @deprecated v1 JWT */
  orgCaps?: TaDeskCapability[];
  /** @deprecated v1 JWT */
  brands?: Record<string, TaDeskCapability[]>;
}

export interface AccessContext {
  userId: string;
  organizationId: string;
  isOwner: boolean;
  capabilities: Set<TaDeskCapability>;
  accessibleBrandIds: string[];
}

export interface RoleTemplate {
  id: RoleTemplateId;
  label: string;
  description: string;
  org: {
    isOwner: boolean;
    capabilities: TaDeskCapability[];
  };
  brandCapabilities: TaDeskCapability[];
}

export interface OrganizationRoleDefinition {
  id: OrganizationRoleId;
  label: string;
  description: string;
  capabilities: TaDeskCapability[];
}

export const ORGANIZATION_ROLES: Record<
  OrganizationRoleId,
  OrganizationRoleDefinition
> = {
  travel_agent: {
    id: "travel_agent",
    label: "Travel Agent",
    description: "Manage own enquiries, quotes, and bookings on assigned brands",
    capabilities: [
      "MANAGE_CONTACTS",
      "MANAGE_ENQUIRIES",
      "MANAGE_QUOTES",
      "MANAGE_BOOKINGS",
    ],
  },
  brand_manager: {
    id: "brand_manager",
    label: "Brand Manager",
    description: "Manage and view team records for assigned brands",
    capabilities: [
      "MANAGE_CONTACTS",
      "MANAGE_ENQUIRIES",
      "MANAGE_QUOTES",
      "MANAGE_QUOTE_TEMPLATES",
      "MANAGE_BOOKINGS",
      "VIEW_TEAM_ENQUIRIES",
      "VIEW_TEAM_QUOTES",
      "VIEW_TEAM_BOOKINGS",
    ],
  },
  bookings_team: {
    id: "bookings_team",
    label: "Bookings Team",
    description: "View all bookings and manage financial operations",
    capabilities: ["VIEW_ALL_BOOKINGS", "MANAGE_BOOKING_FINANCE"],
  },
  reporter: {
    id: "reporter",
    label: "Reporter",
    description: "View reports across the organization",
    capabilities: ["VIEW_REPORTS"],
  },
  accountant: {
    id: "accountant",
    label: "Accountant",
    description: "View and manage accounting across the organization",
    capabilities: ["VIEW_ACCOUNTING", "MANAGE_ACCOUNTING"],
  },
};

export function isOrganizationRoleId(value: string): value is OrganizationRoleId {
  return ORGANIZATION_ROLE_IDS.includes(value as OrganizationRoleId);
}

export function deriveCapabilitiesFromRoles(
  roles: OrganizationRoleId[]
): TaDeskCapability[] {
  const caps = new Set<TaDeskCapability>();
  for (const role of roles) {
    for (const cap of ORGANIZATION_ROLES[role].capabilities) {
      caps.add(cap);
    }
  }
  return [...caps];
}

export function isTaDeskCapability(value: string): value is TaDeskCapability {
  return ALL_TA_DESK_CAPABILITIES.includes(value as TaDeskCapability);
}

export function isValidUserAccessV1(value: unknown): value is iUserAccess {
  if (!value || typeof value !== "object") return false;
  const access = value as iUserAccess;
  if (access.version !== 1) return false;
  if (!access.org || typeof access.org.isOwner !== "boolean") return false;
  if (!Array.isArray(access.org.capabilities)) return false;
  if (!access.org.capabilities.every(isTaDeskCapability)) return false;
  if (!Array.isArray(access.brandAccess)) return false;
  for (const entry of access.brandAccess) {
    if (!entry || typeof entry.brandId !== "string" || !entry.brandId.trim()) {
      return false;
    }
    if (!Array.isArray(entry.capabilities)) return false;
    if (!entry.capabilities.every(isTaDeskCapability)) return false;
  }
  return true;
}

export function isValidUserAccessV2(value: unknown): value is iUserAccessV2 {
  if (!value || typeof value !== "object") return false;
  const access = value as iUserAccessV2;
  if (access.version !== 2) return false;
  if (!access.org || typeof access.org.isOwner !== "boolean") return false;
  if (!Array.isArray(access.org.roles)) return false;
  if (!access.org.roles.every(isOrganizationRoleId)) return false;
  if (!Array.isArray(access.org.capabilities)) return false;
  if (!access.org.capabilities.every(isTaDeskCapability)) return false;
  if (!Array.isArray(access.brandIds)) return false;
  if (!access.brandIds.every((id) => typeof id === "string" && id.trim())) {
    return false;
  }
  return true;
}

export function isValidUserAccess(value: unknown): value is StoredUserAccess {
  return isValidUserAccessV1(value) || isValidUserAccessV2(value);
}

export function buildTokenAccessFromUserAccess(
  access: StoredUserAccess
): iUserTokenAccess {
  if (isValidUserAccessV2(access)) {
    if (access.org.isOwner) {
      return { isOwner: true, capabilities: [], brandIds: [] };
    }
    const capabilities = [
      ...new Set([
        ...deriveCapabilitiesFromRoles(access.org.roles),
        ...access.org.capabilities,
      ]),
    ];
    return {
      isOwner: false,
      capabilities,
      brandIds: [...access.brandIds],
    };
  }

  const v1 = access as iUserAccess;
  if (v1.org.isOwner) {
    return { isOwner: true, capabilities: [], brandIds: [] };
  }

  const capabilities = new Set<TaDeskCapability>(v1.org.capabilities);
  const brandIds: string[] = [];
  for (const entry of v1.brandAccess) {
    const brandId = entry.brandId?.trim();
    if (!brandId) continue;
    brandIds.push(brandId);
    for (const cap of entry.capabilities) {
      capabilities.add(cap);
    }
  }

  return {
    isOwner: false,
    capabilities: [...capabilities],
    brandIds,
  };
}

export interface SerializedAccessContext {
  userId: string;
  organizationId: string;
  isOwner: boolean;
  capabilities: TaDeskCapability[];
  accessibleBrandIds: string[];
}

export interface UserAccessMigrationReport {
  organizationId: string;
  dryRun: boolean;
  scanned: number;
  migrated: number;
  skippedAlreadyMigrated: number;
  skippedNoSourceAccess: number;
  owners: number;
  multiBrandWarnings: Array<{ userId: string; email: string; brandCount: number }>;
  emptyBrandAccess: Array<{ userId: string; email: string; reason: string }>;
  errors: Array<{ userId: string; error: string }>;
}

/** @deprecated Use UserAccessMigrationReport */
export type UserAccessV2MigrationReport = UserAccessMigrationReport;

export function createOwnerAccess(): iUserAccess {
  return {
    org: { isOwner: true, capabilities: [] },
    brandAccess: [],
    version: 1,
  };
}

export function createOwnerAccessV2(): iUserAccessV2 {
  return {
    org: { isOwner: true, roles: [], capabilities: [] },
    brandIds: [],
    version: 2,
  };
}

export function buildUserAccessFromRoles(
  roles: OrganizationRoleId[],
  brandIds: string[],
  extraCapabilities: TaDeskCapability[] = []
): iUserAccessV2 {
  const capabilities = [
    ...deriveCapabilitiesFromRoles(roles),
    ...extraCapabilities,
  ];
  return {
    org: {
      isOwner: false,
      roles: [...roles],
      capabilities: [...new Set(capabilities)],
    },
    brandIds: [...brandIds],
    version: 2,
  };
}
